import React from "react";
import { useParams, Link } from "react-router-dom";
import { cropDetailsData } from "./cropDetailsData";
import { useLang } from "./LangContext";
import "../styles/CropDetail.css";
import { FaArrowLeft, FaCheckCircle, FaExclamationTriangle, FaLeaf, FaBoxOpen } from "react-icons/fa";

const CropDetail = () => {
  const { cropId } = useParams();
  const { t } = useLang();
  
  // Use lowercase to match keys if necessary, or just use the parameter directly
  const data = cropDetailsData[cropId?.toLowerCase()];

  if (!data) {
    return (
      <div className="crop-detail-not-found">
        <h2>Crop Information Not Found</h2>
        <p>Sorry, we don't have information on this crop yet.</p>
        <Link to="/crops" className="back-btn">
           <FaArrowLeft /> Back to Crops
        </Link>
      </div>
    );
  }

  const localizedTitle = t(data.titleKey) || data.title;

  return (
    <div className="crop-detail-container">
      {/* Hero Header */}
      <section 
        className="crop-detail-hero" 
        style={{ backgroundImage: `url(${data.heroImage})` }}
      >
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <Link to="/crops" className="back-link"><FaArrowLeft /> Back</Link>
          <h1>{localizedTitle}</h1>
        </div>
      </section>

      <div className="crop-page-layout">
        
        {/* Packages Section */}
        {data.packages && data.packages.length > 0 && (
          <section className="crop-section">
            <div className="section-header">
              <FaBoxOpen className="section-icon" />
              <h2>Package of Products</h2>
            </div>
            
            <div className="package-grid">
              {data.packages.map((pkg, index) => (
                <div key={index} className="package-card">
                  <h3 className="package-title">{pkg.name}</h3>
                  <p className="package-desc">{pkg.description}</p>
                  
                  {pkg.packSize && (
                    <div className="package-size">
                      <strong>Pack Size:</strong> {pkg.packSize}
                    </div>
                  )}

                  {pkg.contents && pkg.contents.length > 0 && (
                    <div className="package-contents">
                      <strong>Contents:</strong>
                      <ul>
                        {pkg.contents.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {pkg.productLink && (
                     <a href={pkg.productLink} className="detail-action-btn">Read more</a>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Diseases and Solutions Section */}
        {data.diseases && data.diseases.length > 0 && (
          <section className="crop-section disease-section">
            <div className="section-header">
              <FaExclamationTriangle className="section-icon" />
              <h2>Diseases & Deficiencies</h2>
            </div>

            <div className="disease-list">
              {data.diseases.map((disease, index) => (
                <div key={index} className="disease-card">
                  <div className="disease-header">
                    <h3>{disease.name}</h3>
                  </div>
                  <div className="disease-body">
                    
                    <div className="disease-problem">
                      <div className="problem-item">
                        <strong>Symptoms:</strong>
                        <p>{disease.symptoms}</p>
                      </div>
                      <div className="problem-item">
                        <strong>Affect:</strong>
                        <p>{disease.affect}</p>
                      </div>
                    </div>

                    <div className="disease-solution">
                      <h4>Suggested Solution</h4>
                      <div className="solution-bullets">
                        <div className="s-bullet">
                          <span className="s-label">Product:</span> {disease.solution.product}
                        </div>
                        {disease.solution.soilDrip && disease.solution.soilDrip !== "—" && (
                          <div className="s-bullet">
                            <span className="s-label">Soil/Drip:</span> {disease.solution.soilDrip}
                          </div>
                        )}
                        <div className="s-bullet">
                          <span className="s-label">Benefit:</span> {disease.solution.benefit}
                        </div>
                      </div>
                      
                      <div className="recommended-product-footer">
                        {disease.recommendedProduct && (
                          <div className="rec-product-name">
                             <FaLeaf /> <strong>{disease.recommendedProduct}</strong>
                          </div>
                        )}
                        {disease.productLink && (
                          <a href={disease.productLink} className="detail-action-btn sm-btn">Read more</a>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {(!data.packages?.length && !data.diseases?.length) && (
           <div className="no-data-notice">
             <p>Our team is currently preparing details, packages, and solutions for {localizedTitle}. Check back soon!</p>
           </div>
        )}

      </div>
    </div>
  );
};

export default CropDetail;
